import React, { useRef } from 'react';
import html2pdf from 'html2pdf.js';
import './ResumePreview.css';

function ResumePreview({ personalInfo, professionalSummary, experience, education, skills, showDownloadButton = false }) {
  
  // PDF download functionality
  const resumeRef = useRef();

  const handleDownloadPDF = async () => {
    const element = resumeRef.current;
    
    // Standard html2pdf options for optimal A4 output with proper centering
    const opt = {
      margin: [0.2, 0.2, 0.2, 0.2], // 0.4 inch margins for professional look
      filename: `${personalInfo.fullName || 'Resume'}_Resume.pdf`,
      image: { 
        type: 'jpeg', 
        quality: 0.98 
      },
      html2canvas: { 
        scale: 2, // High quality rendering
        useCORS: true,
        letterRendering: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        x: 0,
        y: 0
      },
      jsPDF: { 
        unit: 'in', 
        format: 'a4', 
        orientation: 'portrait',
        compress: true
      },
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: '.avoid-break'
      }
    };

    try {
      // Generate PDF and check for blank pages
      const worker = html2pdf().set(opt).from(element);
      const pdf = await worker.toPdf().get('pdf');
      
      const pageCount = pdf.internal.getNumberOfPages();
      
      // Check if last page is blank and remove it
      if (pageCount > 1) {
        pdf.setPage(pageCount);
        const lastPageHeight = pdf.internal.pageSize.getHeight();
        
        // If content height suggests it should fit on fewer pages, remove blank page
        if (element.scrollHeight < (pageCount - 0.5) * lastPageHeight * 96) {
          pdf.deletePage(pageCount);
          console.log('Removed blank page');
        }
      }
      
      await worker.save();
    } catch (error) {
      console.error('PDF generation error:', error);
      html2pdf().set(opt).from(element).save();
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + '-01');
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // Render based on selected template
  const renderTraditionalTemplate = () => (
    <div className="resume-preview traditional-template">
      {/* Header */}
      <div className="resume-header-traditional avoid-break">
        <h1 className="resume-name-traditional">{personalInfo.fullName || 'Your Name'}</h1>
        <div className="resume-contact-traditional">
          {personalInfo.email && <span>{personalInfo.email}</span>}
          {personalInfo.phone && <span> | {personalInfo.phone}</span>}
          {personalInfo.location && <span> | {personalInfo.location}</span>}
        </div>
        {(personalInfo.linkedIn || personalInfo.portfolio) && (
          <div className="resume-links-traditional">
            {personalInfo.linkedIn && <span>{personalInfo.linkedIn}</span>}
            {personalInfo.portfolio && <span> | {personalInfo.portfolio}</span>}
          </div>
        )}
      </div>

      {/* Professional Summary */}
      {professionalSummary && (
        <div className="resume-section-traditional avoid-break">
          <h2 className="section-title-traditional">PROFESSIONAL SUMMARY</h2>
          <p className="summary-text-traditional">{professionalSummary}</p>
        </div>
      )}

      {/* Experience & Projects */}
      {experience.filter(exp => (exp.type === 'work' && exp.company && exp.position) || (exp.type === 'project' && exp.projectName)).length > 0 && (
        <div className="resume-section-traditional">
          <h2 className="section-title-traditional">EXPERIENCE & PROJECTS</h2>
          {experience.filter(exp => (exp.type === 'work' && exp.company && exp.position) || (exp.type === 'project' && exp.projectName)).map((exp, index) => (
            <div key={index} className="experience-item-traditional avoid-break">
              <div className="experience-header-traditional">
                <div className="experience-left-traditional">
                  <strong style={{ display: 'block', fontWeight: 700 }}>{exp.position}</strong>
                  <em style={{ display: 'block', fontStyle: 'italic', marginTop: '2px' }}>{exp.company}</em>
                </div>
                <span className="date-range-traditional">
                  {exp.startDate && formatDate(exp.startDate)}
                  {' - '}
                  {exp.current ? 'Present' : (exp.endDate ? formatDate(exp.endDate) : '')}
                </span>
              </div>
              {exp.description && (
                <div className="experience-description-traditional">
                  {Array.isArray(exp.description)
                    ? exp.description.map((desc, i) => {
                        // Remove existing bullet symbols and add clean bullet
                        const cleanDesc = desc.replace(/^[-•]\s*/, '');
                        return <div key={i} style={{ marginBottom: '4px' }}>• {cleanDesc}</div>;
                      })
                    : typeof exp.description === 'string'
                    ? exp.description.split(/\s*-\s*/).filter(item => item.trim()).map((desc, i) => {
                        const cleanDesc = desc.replace(/^[-•]\s*/, '').trim();
                        return <div key={i} style={{ marginBottom: '4px' }}>• {cleanDesc}</div>;
                      })
                    : exp.description
                  }
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Education */}
      {education.filter(edu => edu.school && edu.degree).length > 0 && (
        <div className="resume-section-traditional">
          <h2 className="section-title-traditional">EDUCATION</h2>
          {education.filter(edu => edu.school && edu.degree).map((edu, index) => (
            <div key={index} className="education-item-traditional avoid-break">
              <div className="education-left-traditional">
                <strong className="education-degree-traditional">{edu.degree}{edu.field ? ` in ${edu.field}` : ''}</strong>
                <span className="education-university-traditional">{edu.school}</span>
                {edu.gpa && <span className="education-gpa-traditional">GPA: {edu.gpa}</span>}
              </div>
              <div className="education-right-traditional">
                {(edu.startYear || edu.graduationYear) && (
                  <span className="education-dates-traditional">
                    {edu.startYear && edu.graduationYear 
                      ? `${edu.startYear} - ${edu.graduationYear}`
                      : (edu.startYear || edu.graduationYear)
                    }
                  </span>
                )}
                {edu.location && <span className="education-location-traditional">{edu.location}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Skills */}
      {skills && (
        <div className="resume-section-traditional">
          <h2 className="section-title-traditional">SKILLS</h2>
          <p className="skills-text-traditional">{skills}</p>
        </div>
      )}
    </div>
  );

  // Render the selected template
  return (
    <div className="resume-preview-container">
      <div ref={resumeRef} className="pdf-resume-container">
        {renderTraditionalTemplate()}
      </div>
    </div>
  );
}

export default ResumePreview;
